import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Avatar, makeStyles, tokens } from '@fluentui/react-components';
import { DEMO_PERSONAS, findPersona, type CurrentUser } from '@lg/shared';
import { LegendsLogo } from '../brand/LegendsLogo';
import { LEGENDS } from '../brand/theme';
import { safeStorage } from '../storage';
import { AuthContext, type AuthState } from './AuthContext';

const PERSONA_KEY = 'lg.demoPersona';

// The landing page always uses the dark "Venue Factory Command" skin.
const useStyles = makeStyles({
  page: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '28px',
    padding: '48px 24px',
    boxSizing: 'border-box',
    color: LEGENDS.navyText,
    backgroundColor: LEGENDS.navy,
    backgroundImage: `radial-gradient(1200px 500px at 50% -10%, #10283A 0%, ${LEGENDS.navy} 60%)`,
  },
  hero: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center' },
  rule: { width: '120px', height: '3px', backgroundColor: LEGENDS.blue, borderRadius: '2px' },
  title: { margin: 0, fontSize: '2.1rem', fontWeight: 700, lineHeight: 1.2, color: LEGENDS.navyText },
  tagline: { fontSize: '1.05rem', color: LEGENDS.navyMuted, maxWidth: '640px', lineHeight: 1.45 },
  kicker: {
    color: LEGENDS.blue,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.08em',
    fontSize: tokens.fontSizeBase200,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '16px',
    width: 'min(1080px, 100%)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: LEGENDS.navyText,
    backgroundColor: LEGENDS.navyPanel,
    border: `1px solid ${LEGENDS.navyLine}`,
    borderLeft: `4px solid ${LEGENDS.blue}`,
    borderRadius: '10px',
    padding: '16px 18px',
    transitionProperty: 'background-color, transform',
    transitionDuration: '120ms',
    ':hover': { backgroundColor: LEGENDS.navyPanel2, transform: 'translateY(-2px)' },
    ':focus-visible': { outline: `3px solid ${LEGENDS.blue}`, outlineOffset: '3px' },
  },
  who: { display: 'flex', alignItems: 'center', gap: '12px' },
  name: { fontWeight: 700, fontSize: '1.05rem' },
  upn: { fontSize: tokens.fontSizeBase200, color: LEGENDS.navyDim },
  does: { fontSize: tokens.fontSizeBase300, color: LEGENDS.navyMuted, lineHeight: 1.4 },
  roles: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: 'auto' },
  role: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: LEGENDS.blue,
    border: `1px solid ${LEGENDS.blue}`,
    borderRadius: '4px',
    padding: '1px 6px',
  },
  foot: { fontSize: tokens.fontSizeBase200, color: LEGENDS.navyDim },
});

const PERSONA_DOES: Record<string, string> = {
  'riley.chen@legends.example':
    'Starts the venue request, completes the intake and loads people and approvals.',
  'morgan.patel@legends.example': 'Finance controller: reviews deviations and approval limits at gate 2.',
  'jordan.alvarez@legends.example': 'D365 administrator: approves gate 3, confirms and runs provisioning.',
  'casey.brooks@legends.example': 'Application administrator: templates, routing and settings.',
};

export function PersonaPicker({ onPick }: { onPick: (p: CurrentUser) => void }) {
  const styles = useStyles();
  return (
    <main className={styles.page}>
      <div className={styles.hero}>
        <LegendsLogo tone="onDark" height={72} />
        <span className={styles.rule} />
        <span className={styles.kicker}>Venue factory</span>
        <h1 className={styles.title} data-hero>
          Venue onboarding
        </h1>
        <span className={styles.tagline}>
          From signed venue contract to a ready-to-transact company in Dynamics 365: intake, people and
          approvals, four readiness gates, then provisioning from the template.
        </span>
      </div>
      <div className={styles.grid}>
        {DEMO_PERSONAS.map((p) => (
          <button
            key={p.upn}
            type="button"
            className={styles.card}
            onClick={() => onPick(p)}
            aria-label={`Continue as ${p.name}`}
          >
            <span className={styles.who}>
              <Avatar name={p.name} color="brand" />
              <span>
                <span className={styles.name}>{p.name}</span>
                <br />
                <span className={styles.upn}>{p.upn}</span>
              </span>
            </span>
            <span className={styles.does}>{PERSONA_DOES[p.upn]}</span>
            <span className={styles.roles}>
              {p.roles.map((r) => (
                <span key={r} className={styles.role}>
                  {r}
                </span>
              ))}
            </span>
          </button>
        ))}
      </div>
      <span className={styles.foot}>Choose who to be. Switch at any time from the account menu.</span>
    </main>
  );
}

/** Demo persona switch for local dev and the static demo. No credentials involved. */
export function MockAuthGate({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => {
    // `?as=<upn>` starts as that persona (handy for demo links, one per role).
    const requested = new URLSearchParams(window.location.search).get('as');
    const fromLink = requested ? findPersona(requested) : undefined;
    if (fromLink) {
      safeStorage.set(PERSONA_KEY, fromLink.upn);
      return fromLink;
    }
    const saved = safeStorage.get(PERSONA_KEY);
    return saved ? (findPersona(saved) ?? null) : null;
  });

  const pick = useCallback((p: CurrentUser) => {
    safeStorage.set(PERSONA_KEY, p.upn);
    setUser(p);
  }, []);

  const state = useMemo<AuthState | null>(
    () =>
      user && {
        user,
        getAuthHeaders: async () => ({ 'x-mock-user': user.upn }),
        switchPersona: () => {
          safeStorage.remove(PERSONA_KEY);
          setUser(null);
        },
      },
    [user],
  );

  if (!state) return <PersonaPicker onPick={pick} />;
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
